import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  ChevronLeft,
  MapPin,
  Phone,
  Mail,
  ExternalLink,
  Building2,
  Star,
  Package,
  Clock,
  CheckCircle,
} from "lucide-react";
import { motion } from "framer-motion";
import { getClinicById, getProducts, getApprovedReviews } from "../lib/api";
import type { Clinic, ProductWithStats, ReviewWithRelations } from "../lib/types";
import { toast } from "sonner";

interface ClinicDetailProps {
  clinicId?: string;
  onNavigate?: (page: string, id?: string) => void;
}

export function ClinicDetail({ clinicId, onNavigate }: ClinicDetailProps) {
  const [clinic, setClinic] = useState<Clinic | null>(null);
  const [products, setProducts] = useState<ProductWithStats[]>([]);
  const [reviews, setReviews] = useState<ReviewWithRelations[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState("overview");

  useEffect(() => {
    const fetchClinicData = async () => {
      if (!clinicId) {
        // Keep loading until we get a clinicId
        return;
      }

      try {
        setLoading(true);
        const clinicData = await getClinicById(clinicId);

        if (!clinicData) {
          toast.error("Clinic not found");
          onNavigate && onNavigate("clinics");
          return;
        }

        setClinic(clinicData);

        // Note: In a real implementation, you'd filter products by clinic_id
        // For now, we'll show all active products since the schema doesn't have clinic_id on products
        const [productsData, reviewsData] = await Promise.all([
          getProducts({ active: true }),
          getApprovedReviews({}),
        ]);

        setProducts(productsData.slice(0, 6)); // Show first 6 products
        setReviews(reviewsData.slice(0, 5)); // Show first 5 reviews
      } catch (error: any) {
        console.error("Error fetching clinic:", error);
        toast.error("Failed to load clinic details");
      } finally {
        setLoading(false);
      }
    };

    fetchClinicData();
  }, [clinicId, onNavigate]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
        <div className="max-w-7xl mx-auto">
          <Skeleton className="h-10 w-48 mb-6" />
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-64 w-full rounded-2xl" />
              <Skeleton className="h-96 w-full rounded-2xl" />
            </div>
            <div className="space-y-6">
              <Skeleton className="h-48 w-full rounded-2xl" />
              <Skeleton className="h-64 w-full rounded-2xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!clinic) {
    return null;
  }

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Button
            variant="ghost"
            className="mb-6 hover:bg-primary/10"
            onClick={() => onNavigate && onNavigate("clinics")}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Clinics
          </Button>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Clinic Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Card className="p-8 glass-card">
                <div className="flex items-start gap-6">
                  <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-10 h-10 text-primary" />
                  </div>

                  <div className="flex-1">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {clinic.is_active && (
                        <Badge className="gradient-green text-white">Active</Badge>
                      )}
                      {clinic.licence_number && (
                        <Badge variant="outline">Licensed</Badge>
                      )}
                    </div>

                    <h1 className="text-3xl mb-4">{clinic.name}</h1>

                    <div className="space-y-3 text-muted-foreground">
                      {clinic.address && (
                        <div className="flex items-start gap-3">
                          <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                          <div>
                            <p>{clinic.address}</p>
                            {(clinic.city || clinic.county || clinic.postcode) && (
                              <p>
                                {clinic.city && clinic.city}
                                {clinic.county && `, ${clinic.county}`}
                                {clinic.postcode && ` ${clinic.postcode}`}
                              </p>
                            )}
                            {clinic.country && <p>{clinic.country}</p>}
                          </div>
                        </div>
                      )}

                      {clinic.phone && (
                        <div className="flex items-center gap-3">
                          <Phone className="w-5 h-5 flex-shrink-0" />
                          <a href={`tel:${clinic.phone}`} className="hover:text-primary transition-colors">
                            {clinic.phone}
                          </a>
                        </div>
                      )}

                      {clinic.email && (
                        <div className="flex items-center gap-3">
                          <Mail className="w-5 h-5 flex-shrink-0" />
                          <a href={`mailto:${clinic.email}`} className="hover:text-primary transition-colors">
                            {clinic.email}
                          </a>
                        </div>
                      )}

                      {clinic.website && (
                        <div className="flex items-center gap-3">
                          <ExternalLink className="w-5 h-5 flex-shrink-0" />
                          <a
                            href={clinic.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-primary transition-colors"
                          >
                            {clinic.website.replace(/^https?:\/\//, "")}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* Tabs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
            >
              <Tabs value={selectedTab} onValueChange={setSelectedTab}>
                <TabsList className="grid w-full grid-cols-2 glass-card">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="products">Products ({products.length})</TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-6">
                  {clinic.licence_number && (
                    <Card className="p-6 glass-card">
                      <h3 className="text-lg mb-4">Licence Information</h3>
                      <div className="p-4 rounded-xl bg-muted/30">
                        <p className="text-sm text-muted-foreground mb-1">Licence Number</p>
                        <p className="font-mono">{clinic.licence_number}</p>
                      </div>
                    </Card>
                  )}

                  <Card className="p-6 glass-card">
                    <h3 className="text-lg mb-4">About This Clinic</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {clinic.name} is a licensed medical cannabis clinic committed to providing quality care
                      and products to patients. Our clinic maintains the highest standards of professionalism
                      and patient care.
                    </p>
                  </Card>

                  <Card className="p-6 glass-card">
                    <h3 className="text-lg mb-4 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-primary" />
                      Why Choose Us
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/30">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium mb-1">Licensed & Regulated</p>
                          <p className="text-sm text-muted-foreground">
                            Fully licensed and compliant with all regulations
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/30">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium mb-1">Quality Products</p>
                          <p className="text-sm text-muted-foreground">
                            Carefully selected, tested cannabis products
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/30">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium mb-1">Patient-Focused Care</p>
                          <p className="text-sm text-muted-foreground">
                            Dedicated to your health and wellbeing
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/30">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium mb-1">Expert Staff</p>
                          <p className="text-sm text-muted-foreground">
                            Knowledgeable professionals ready to help
                          </p>
                        </div>
                      </div>
                    </div>
                  </Card>
                </TabsContent>

                {/* Products Tab */}
                <TabsContent value="products" className="space-y-4">
                  {products.length === 0 ? (
                    <Card className="p-12 glass-card text-center">
                      <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                      <h3 className="text-lg mb-2">No products available</h3>
                      <p className="text-sm text-muted-foreground">
                        This clinic doesn't have any products listed yet.
                      </p>
                    </Card>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                      {products.map((product, index) => {
                        const reviewCount = product.rating_stats?.[0]?.review_count || 0;
                        const avgStars = product.rating_stats?.[0]?.avg_stars || 0;

                        return (
                          <motion.div
                            key={product.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                          >
                            <Card
                              className="p-6 glass-card hover-lift cursor-pointer group"
                              onClick={() => onNavigate && onNavigate("product-detail", product.id)}
                            >
                              <div className="flex items-start justify-between mb-3">
                                <Badge className="capitalize">{product.form}</Badge>
                                {product.badge?.[0]?.qualifies_for_badge && (
                                  <Badge className="gradient-green text-white text-xs">
                                    Top Rated
                                  </Badge>
                                )}
                              </div>

                              <h4 className="text-lg mb-2 group-hover:text-primary transition-colors line-clamp-1">
                                {product.product_name}
                              </h4>
                              <p className="text-sm text-muted-foreground mb-4">
                                {product.brand || "Various Brands"}
                              </p>

                              <div className="grid grid-cols-3 gap-2 mb-4">
                                <div className="text-center p-2 rounded-lg bg-muted/30">
                                  <p className="text-xs text-muted-foreground">THC</p>
                                  <p className="text-sm font-medium">
                                    {product.thc_pct ? `${product.thc_pct}%` : "N/A"}
                                  </p>
                                </div>
                                <div className="text-center p-2 rounded-lg bg-muted/30">
                                  <p className="text-xs text-muted-foreground">CBD</p>
                                  <p className="text-sm font-medium">
                                    {product.cbd_pct ? `${product.cbd_pct}%` : "N/A"}
                                  </p>
                                </div>
                                <div className="text-center p-2 rounded-lg bg-muted/30">
                                  <p className="text-xs text-muted-foreground">Reviews</p>
                                  <p className="text-sm font-medium">{reviewCount}</p>
                                </div>
                              </div>

                              {avgStars > 0 && (
                                <div className="flex items-center gap-1">
                                  <Star className="w-4 h-4 fill-current text-primary" />
                                  <span className="text-sm font-medium">{avgStars.toFixed(1)}</span>
                                  <span className="text-sm text-muted-foreground">/5</span>
                                </div>
                              )}
                            </Card>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Card className="p-6 glass-card">
                <h3 className="text-lg mb-4">Contact Information</h3>
                <div className="space-y-4">
                  {clinic.phone && (
                    <Button className="w-full gradient-green hover:opacity-90 transition-opacity" asChild>
                      <a href={`tel:${clinic.phone}`}>
                        <Phone className="w-4 h-4 mr-2" />
                        Call Clinic
                      </a>
                    </Button>
                  )}
                  {clinic.email && (
                    <Button variant="outline" className="w-full" asChild>
                      <a href={`mailto:${clinic.email}`}>
                        <Mail className="w-4 h-4 mr-2" />
                        Send Email
                      </a>
                    </Button>
                  )}
                  {clinic.website && (
                    <Button variant="outline" className="w-full" asChild>
                      <a href={clinic.website} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Visit Website
                      </a>
                    </Button>
                  )}
                </div>
              </Card>
            </motion.div>

            {/* Location Card */}
            {clinic.address && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1, duration: 0.5 }}
              >
                <Card className="p-6 glass-card">
                  <h3 className="text-lg mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />
                    Location
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p>{clinic.address}</p>
                    {(clinic.city || clinic.county || clinic.postcode) && (
                      <p className="text-muted-foreground">
                        {clinic.city && clinic.city}
                        {clinic.county && `, ${clinic.county}`}
                        {clinic.postcode && ` ${clinic.postcode}`}
                      </p>
                    )}
                    {clinic.country && (
                      <p className="text-muted-foreground">{clinic.country}</p>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    className="w-full mt-4"
                    onClick={() => {
                      const address = `${clinic.address}, ${clinic.city}, ${clinic.county} ${clinic.postcode}`;
                      window.open(
                        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`,
                        "_blank"
                      );
                    }}
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    Get Directions
                  </Button>
                </Card>
              </motion.div>
            )}

            {/* Stats Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <Card className="p-6 glass-card">
                <h3 className="text-lg mb-4">Quick Stats</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                    <span className="text-sm text-muted-foreground">Products</span>
                    <span className="font-medium">{products.length}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                    <span className="text-sm text-muted-foreground">Status</span>
                    <Badge variant={clinic.is_active ? "default" : "secondary"}>
                      {clinic.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  {clinic.licence_number && (
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                      <span className="text-sm text-muted-foreground">Licensed</span>
                      <CheckCircle className="w-5 h-5 text-primary" />
                    </div>
                  )}
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}